/**
 * The My Teaching landing page: your day, and what you still owe.
 *
 * The sidebar lists the classes you teach, so this page deliberately does not.
 * It answers the two questions the sidebar cannot: where am I supposed to be
 * right now, and what is outstanding across all of it. The category pages next
 * to it take one kind of work across every class; this one takes one day.
 */
import { useMemo } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { formatClockTime } from '../Schedules/scheduleUtils';
import type { TeachingContext } from './teachingContext';
import { buildDay, nextMeetingIndex, type DayMeeting } from './teachingDay';
import TeachingLoadError from './TeachingLoadError';

/** Which category page answers each kind of outstanding work. */
const INBOX_SECTION: Record<string, string> = {
  TO_GRADE: 'grades',
  ATTENDANCE_MISSING: 'attendance',
  DUE_TODAY: 'assignments',
};

function shiftDate(date: string, days: number) {
  const [year, month, day] = date.split('-').map(Number);
  const shifted = new Date(year, month - 1, day + days);
  return [
    shifted.getFullYear(),
    String(shifted.getMonth() + 1).padStart(2, '0'),
    String(shifted.getDate()).padStart(2, '0'),
  ].join('-');
}

function longDate(date: string) {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

function rollStatus(meeting: DayMeeting) {
  const roll = meeting.roll;
  if (!roll) return null;
  if (!roll.attendanceTaken) return null;
  return roll.expectedStudentCount > 0
    ? `Attendance taken · ${roll.attendanceRecordedCount} of ${roll.expectedStudentCount}`
    : 'Attendance taken';
}

export default function TeachingOverview() {
  const { schoolSlug, yearSlug } = useParams();
  const {
    classes, attendanceRolls, inboxItems, loading,
    schoolYearName, schoolYearStart, schoolYearEnd,
    rollDate, setRollDate, today, nowTime, error,
  } = useOutletContext<TeachingContext>();

  const basePath = `/app/${schoolSlug}/years/${yearSlug}/teaching`;
  const meetings = useMemo(() => buildDay(classes, attendanceRolls, rollDate), [classes, attendanceRolls, rollDate]);
  const isToday = rollDate === today;
  const nowIndex = isToday ? nextMeetingIndex(meetings, nowTime) : -1;

  const canGoBack = !schoolYearStart || shiftDate(rollDate, -1) >= schoolYearStart;
  const canGoForward = !schoolYearEnd || shiftDate(rollDate, 1) <= schoolYearEnd;

  if (error) return <TeachingLoadError error={error} />;

  // Someone with no teaching at all gets one explanation, not an empty day and
  // an empty queue stacked on top of it.
  if (!loading && classes.length === 0 && attendanceRolls.length === 0 && inboxItems.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No classes assigned yet</CardTitle>
          <CardDescription>
            You aren't assigned to classes or attendance rolls in {schoolYearName}. An admin can add those assignments.
          </CardDescription>
        </CardHeader>
        <CardContent />
      </Card>
    );
  }

  return (
    <div className="space-y-10">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon" className="h-8 w-8"
              disabled={!canGoBack}
              onClick={() => setRollDate(shiftDate(rollDate, -1))}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="min-w-0 text-xl font-semibold tracking-tight">
              {isToday ? 'Today' : longDate(rollDate)}
            </h2>
            <Button
              variant="ghost" size="icon" className="h-8 w-8"
              disabled={!canGoForward}
              onClick={() => setRollDate(shiftDate(rollDate, 1))}
              aria-label="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            {isToday && <p className="text-sm text-muted-foreground">{longDate(rollDate)}</p>}
            {!isToday && (
              <Button variant="outline" size="sm" onClick={() => setRollDate(today)}>
                Back to today
              </Button>
            )}
          </div>
        </div>

        {loading && meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading your day…</p>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing is scheduled for you on this day.
          </p>
        ) : (
          <ol className="border-l">
            {meetings.map((meeting, index) => {
              const isNext = index === nowIndex;
              const status = rollStatus(meeting);
              // A meeting recedes once it is over, unless it left something
              // undone — the roll you still owe is the last thing to fade.
              const outstanding = Boolean(meeting.roll && !meeting.roll.attendanceTaken);
              const isPast = isToday && nowIndex >= 0 && index < nowIndex && !outstanding;
              const classPath = meeting.classOffering
                ? `/app/${schoolSlug}/years/${yearSlug}/courses/${meeting.classOffering.course?.slug}/classes/${meeting.classOffering.id}`
                : undefined;

              return (
                <li
                  key={meeting.blockId}
                  className={`relative flex flex-wrap items-baseline gap-x-6 gap-y-2 py-4 pl-6 pr-1 ${
                    isNext ? 'before:absolute before:inset-y-0 before:-left-px before:w-0.5 before:bg-primary' : ''
                  } ${isPast ? 'opacity-60' : ''}`}
                >
                  <div className="w-24 shrink-0 tabular-nums">
                    <p className={`text-base ${isNext ? 'font-semibold' : 'font-medium'}`}>
                      {formatClockTime(meeting.startsAt)}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatClockTime(meeting.endsAt)}</p>
                  </div>

                  <div className="min-w-[12rem] flex-1 space-y-0.5">
                    <p className="font-medium">
                      {classPath ? (
                        <Link to={classPath} className="hover:underline">{meeting.title}</Link>
                      ) : meeting.title}
                    </p>
                    {meeting.subtitle && (
                      <p className="text-sm text-muted-foreground">{meeting.subtitle}</p>
                    )}
                    <p className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                      {meeting.classroom ?? 'Location not assigned'}
                    </p>
                  </div>

                  <div className="shrink-0">
                    {meeting.roll && !meeting.roll.attendanceTaken && (
                      <Button asChild size="sm" variant={isNext || index < nowIndex ? 'default' : 'outline'}>
                        <Link to={`/app/${schoolSlug}/years/${yearSlug}/attendance/${meeting.blockId}?date=${meeting.roll.date}`}>
                          Take attendance
                        </Link>
                      </Button>
                    )}
                    {status && (
                      <Link
                        to={`/app/${schoolSlug}/years/${yearSlug}/attendance/${meeting.blockId}?date=${meeting.roll?.date}`}
                        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                      >
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                        {status}
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">Needs you</h2>
        {inboxItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {loading ? 'Checking your classes…' : `You're caught up across your classes in ${schoolYearName}.`}
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {inboxItems.map((item) => (
              <li key={item.kind}>
                <Link
                  to={`${basePath}/${INBOX_SECTION[item.kind] ?? ''}`}
                  className="flex items-center gap-4 p-4 transition-colors hover:bg-muted/50"
                >
                  <span
                    aria-hidden="true"
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      item.urgency === 'warning' ? 'bg-amber-500' : 'bg-muted-foreground/40'
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{item.title}</span>
                    <span className="block truncate text-sm text-muted-foreground">{item.detail}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

    </div>
  );
}
